import { observer } from 'mobx-react';

import { WidgetCrownBadge } from '../../../features/widgets/components/JackpotWidget/JackpotWidgetArt';
import { useStores } from '../../../stores/useStores';
import { SocialCoin } from '../../SocialCoin/SocialCoin';
import { SweepCoin } from '../../SweepCoin/SweepCoin';

/*
 * The badge's LOOK, in full — the capsule, the type, the mark and coin sizes — shared with the
 * provider-feed badge that takes the same corner on other tiles. This component adds no styling of
 * its own beyond the overlay box and the visibility gate in its module below.
 *
 * Imported here as well as by `JackpotBalance` because the two are mutually exclusive per tile: a
 * lobby of Crown-only tiles never renders `JackpotBalance` at all, and would otherwise draw an
 * unstyled badge. The bundler emits one stylesheet either way.
 */
import '../../JackpotBalance/JackpotBalance.scss';

import styles from './CrownJackpotBadge.module.scss';

/**
 * Crown Jackpot badge on a lobby game tile — frame `9787:5634`, the badge group inside the
 * `Thumbnail` component `10083:13431`.
 *
 * A crown wheel at the left end of a pill carrying the coin and the top level pot — the biggest prize
 * on the ladder, by association priority. Same slot the legacy Crownslots `JackpotBalance` badge uses,
 * and mutually exclusive with it: a tile shows one jackpot or the other, never both stacked. It
 * renders that badge's own markup, class for class, so the two cannot drift apart.
 *
 * ## Visibility is not decided here
 * WHETHER this is drawn is CSS — see the module's `.host` rules. The caller renders it for any game
 * whose `jackpots` array is non-empty, and two `<body>` attributes (`data-jp-badges`, `data-jp-coin`,
 * both published by `JackpotStore`) decide whether the tile's own `data-jackpot-coins` matches.
 *
 * That matters because a game is normally enrolled on BOTH coins while only one jackpot is live, and
 * because the gate flips on maintenance and on the widget's pacing decision. Reading either from the
 * store here would make every tile in the lobby an observer, re-rendering the lot to change nothing
 * but `display`.
 *
 * What is still read here is the AMOUNT — a live figure that genuinely changes on the 30s pot poll,
 * and the one thing on this badge that has to come from the store.
 */
function CrownJackpotBadgeComponent() {
  const { coinTypeStore, jackpotStore } = useStores();

  // Its own budget — the popup ladder's, so a badged thumbnail and the ladder behind it agree.
  const { topLevelAmountLabelBadge } = jackpotStore;

  // The platform coin, not the jackpot one: `GC` is the gold wallet, whose jackpot is `CC`.
  const Coin = coinTypeStore.coinType === 'GC' ? SocialCoin : SweepCoin;

  return (
    /*
      The host exists to position and to gate. It carries the test id because it is the element the
      tile owns; everything visible hangs off the shared badge class inside it.
    */
    <div className={styles.host} data-testid="crown-jackpot-tile-badge">
      <div className="jackpot-balance">
        <WidgetCrownBadge className="jackpot-balance-provider-icon jackpot-balance-provider-icon--crown" />
        <Coin className="jackpot-balance-coin-picture" />
        <div>{topLevelAmountLabelBadge}</div>
      </div>
    </div>
  );
}

export const CrownJackpotBadge = observer(CrownJackpotBadgeComponent);
